import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PdfViewerConfig, ZoomPreset } from './pdf-viewer.types';
import { Subject } from 'rxjs';
import { ClickOutsideDirective } from "../../directives/click-outside";

@Component({
  selector: 'pdf-preview',
  imports: [NgxExtendedPdfViewerModule, CommonModule, ClickOutsideDirective],
  templateUrl: './pdf-preview.html',
  styleUrl: './pdf-preview.scss',
})
export class PdfPreview implements OnDestroy, OnChanges, OnInit {
  @Input() config!: PdfViewerConfig;
  @Input() isOverlay = false;

  @Output() close = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  currentPage = signal(1);
  totalPages = signal(0);
  zoom = signal<ZoomPreset>(1);
  isFullscreen = signal(false);
  isLoading = signal(true);
  showZoomMenu = signal(false);
  hasError = signal(false);

  safeSrc = signal<string | Uint8Array>('');
  private loadingTimeout?: ReturnType<typeof setTimeout>;
  private previousSrc: string | Uint8Array | null = null;

  zoomValue = computed(() => {
    const z = this.zoom();
    if (z === 'fit-width') return 'page-width';
    if (z === 'fit-page') return 'page-fit';
    return `${Math.round((z as number) * 100)}%`;
  });

  zoomLabel = computed(() => {
    const z = this.zoom();
    if (z === 'fit-width') return 'Fit Width';
    if (z === 'fit-page') return 'Fit Page';
    return `${Math.round((z as number) * 100)}%`;
  });

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOverlay) this.onClose();
  }

  @HostListener('document:keydown.arrowright')
  onArrowRight() {
    this.nextPage();
  }

  @HostListener('document:keydown.arrowleft')
  onArrowLeft() {
    this.prevPage();
  }

  ngOnInit() {
    this.currentPage.set(Math.max(1, this.config?.initialPage ?? 1));
    this.zoom.set(this.config?.initialZoom ?? 1);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  ngOnChanges() {
    if (this.config?.src && this.config.src !== this.previousSrc) {
      this.previousSrc = this.config.src;
      this.isLoading.set(true);
      this.hasError.set(false);
      this.currentPage.set(this.config?.initialPage ?? 1);
      this.totalPages.set(0);

      this.clearLoadingTimeout();
      this.loadingTimeout = setTimeout(() => {
        this.isLoading.set(false);
      }, 10000);
    }
  }

  ngOnDestroy() {
    this.clearLoadingTimeout();
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onFullscreenChange = () => {
    if (!document.fullscreenElement) {
      this.isFullscreen.set(false);
    }
  };

  onPagesLoaded(event: { pagesCount: number }) {
    this.totalPages.set(event.pagesCount);
    this.clearLoadingTimeout();
    this.isLoading.set(false);
  }

  onPageRendered() {
    this.clearLoadingTimeout();
    this.isLoading.set(false);
  }

  onError(error: any) {
    console.error('PDF error:', error);
    this.clearLoadingTimeout();
    this.isLoading.set(false);
    this.hasError.set(true);
  }

  private clearLoadingTimeout() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = undefined;
    }
  }

  onCurrentPageChanged(page: any) {
    this.currentPage.set(page);
  }

  nextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
    }
  }

  prevPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
    }
  }

  goToPage(event: Event) {
    const input = event.target as HTMLInputElement;
    const page = parseInt(input.value, 10);
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    } else {
      input.value = this.currentPage().toString();
    }
  }

  blurInput(event: Event) {
    (event.target as HTMLInputElement).blur();
  }

  zoomIn() {
    const current = this.zoom();
    if (typeof current === 'number') {
      this.zoom.set(Math.min(current + 0.25, 4));
    } else {
      this.zoom.set(1.25);
    }
  }

  zoomOut() {
    const current = this.zoom();
    if (typeof current === 'number') {
      this.zoom.set(Math.max(current - 0.25, 0.25));
    } else {
      this.zoom.set(0.75);
    }
  }

  setZoom(value: ZoomPreset) {
    this.zoom.set(value);
    this.showZoomMenu.set(false);
  }

  toggleZoomMenu() {
    this.showZoomMenu.update((v) => !v);
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      this.isFullscreen.set(true);
    } else {
      document.exitFullscreen?.();
      this.isFullscreen.set(false);
    }
  }

  private async toBlobUrl(src: string | Uint8Array): Promise<string> {
    if (typeof src === 'string') {
      const response = await fetch(src);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
    const arrayBuffer = src.slice().buffer;
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }

  async downloadPdf() {
    const blobUrl = await this.toBlobUrl(this.config.src);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = this.config.filename || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  async printPdf() {
    const blobUrl = await this.toBlobUrl(this.config.src);
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;';
    iframe.src = blobUrl;
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      }, 500);
    };
    document.body.appendChild(iframe);
  }

  onClose() {
    this.close.emit();
  }

  onBackdropClick() {
    if (this.isOverlay) this.onClose();
  }
}
